import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useLanguage } from "../contexts/LanguageContext";
import { useUnsavedChanges } from "../contexts/UnsavedChangesContext";

/**
 * L'app aperta si accorge dei rilasci nuovi, senza chiuderla e riaprirla.
 *
 * Ogni build ha una versione — il commit, `REACT_APP_SW_VERSION`, scritta da
 * `scripts/set-sw-version.js` — e `index.js` la confronta con quella salvata
 * **all'avvio**: chi apre l'app dopo un rilascio prende il bundle nuovo. Ma
 * un'app che resta aperta — la PWA ripresa dal background, una scheda
 * lasciata lì — non riparte mai: il suo `registration.update()` riscarica il
 * service worker al proprio URL, che non cambia (la versione sta nella query
 * string), e l'atleta resta sulla versione vecchia per giorni. È successo: un
 * atleta vedeva il profilo prestativo com'era nella build precedente.
 *
 * Qui l'app aperta chiede a `/version.json` — scritto dalla stessa build,
 * mai in cache: cfr. `public/service-worker.js` e `vercel.json` — se è uscito
 * un rilascio: quando torna in primo piano, e ogni tanto mentre è visibile.
 * Se c'è:
 *  - al rientro dopo un'assenza lunga ricarica subito, senza chiedere: non
 *    c'è niente in corso da perdere, ed è il caso dell'atleta che riapre
 *    l'app il giorno dopo;
 *  - altrimenti avvisa con un toast («Aggiorna») e applica da sé alla prima
 *    navigazione interna, quando la pagina viene comunque smontata.
 * Mai con modifiche non salvate in giro (`UnsavedChangesContext`), e mai due
 * ricaricamenti automatici per la stessa versione nella stessa sessione: se
 * dopo il reload arriva ancora il bundle vecchio, resta solo il toast.
 *
 * Fuori dalle build di produzione non fa niente.
 */

const BUILT_VERSION = process.env.REACT_APP_SW_VERSION || null;
const ENABLED = process.env.NODE_ENV === "production" && Boolean(BUILT_VERSION);

/** Dopo quanto, lontani dall'app, al rientro si ricarica senza chiedere. */
export const RESUME_RELOAD_AFTER_MS = 5 * 60 * 1000;
const CHECK_EVERY_MS = 15 * 60 * 1000;
const MIN_GAP_BETWEEN_CHECKS_MS = 60 * 1000;
const RELOADED_FOR_KEY = "kraveon_update_reload_for";
const TOAST_ID = "app-update";

/**
 * Cosa fare con la versione letta dal server. Pura, per i test.
 *
 * @param {object} p
 * @param {string|null} p.latest  versione pubblicata
 * @param {string|null} p.built   versione di questo bundle
 * @param {number} [p.hiddenForMs] da quanto l'app era fuori dallo schermo
 * @param {boolean} [p.navigating] siamo a una navigazione interna
 * @param {boolean} [p.hasUnsaved] qualcuno ha modifiche non salvate
 * @param {boolean} [p.alreadyReloaded] in questa sessione abbiamo già
 *   ricaricato per questa stessa versione
 * @returns {"none"|"reload"|"notify"}
 */
export function planUpdate({
	latest,
	built,
	hiddenForMs = 0,
	navigating = false,
	hasUnsaved = false,
	alreadyReloaded = false,
}) {
	if (!latest || !built || latest === built) return "none";
	if (hasUnsaved || alreadyReloaded) return "notify";
	if (navigating || hiddenForMs >= RESUME_RELOAD_AFTER_MS) return "reload";
	return "notify";
}

/** La versione pubblicata, o null se non si riesce a leggerla (offline…). */
export async function fetchLatestVersion(fetchImpl = globalThis.fetch) {
	try {
		// Il parametro scavalca anche i service worker vecchi, che servivano
		// i file statici dalla cache senza eccezioni.
		const response = await fetchImpl(`/version.json?t=${Date.now()}`, {
			cache: "no-store",
		});
		if (!response?.ok) return null;
		const data = await response.json();
		const version = String(data?.version || "").trim();
		return version || null;
	} catch {
		return null;
	}
}

const alreadyReloadedFor = (version) => {
	try {
		return window.sessionStorage.getItem(RELOADED_FOR_KEY) === version;
	} catch {
		return false;
	}
};

const reloadTo = (version) => {
	try {
		window.sessionStorage.setItem(RELOADED_FOR_KEY, version);
	} catch {
		// Senza sessionStorage si perde solo la protezione dal doppio reload.
	}
	toast.dismiss(TOAST_ID);
	window.location.reload();
};

// Stato di modulo, non di componente: deve sopravvivere ai remount e vale
// una volta sola per la pagina.
let pendingVersion = null;
let hiddenAt = null;
let lastCheckAt = 0;

export function useAppUpdateCheck() {
	const { t } = useLanguage();
	const unsaved = useUnsavedChanges();
	const { pathname } = useLocation();

	const translate = useRef(t);
	const hasUnsaved = useRef(() => false);
	const confirmLeave = useRef(() => true);
	useEffect(() => {
		translate.current = t;
		hasUnsaved.current = unsaved?.hasUnsaved || (() => false);
		confirmLeave.current = unsaved?.confirmLeave || (() => true);
	}, [t, unsaved]);

	useEffect(() => {
		if (!ENABLED) return undefined;

		const notify = (version) => {
			// Una volta per versione: chi ha chiuso il toast non se lo ritrova
			// a ogni cambio di finestra; l'aggiornamento arriva comunque alla
			// prossima navigazione o al rientro dopo un'assenza lunga.
			if (pendingVersion === version) return;
			pendingVersion = version;
			toast(translate.current("appUpdateTitle"), {
				id: TOAST_ID,
				description: translate.current("appUpdateBody"),
				duration: Infinity,
				action: {
					label: translate.current("appUpdateAction"),
					onClick: () => {
						if (confirmLeave.current()) reloadTo(version);
					},
				},
			});
		};

		const act = (plan, version) => {
			if (plan === "reload") reloadTo(version);
			else if (plan === "notify") notify(version);
		};

		const check = async () => {
			const now = Date.now();
			let hiddenForMs = 0;
			if (hiddenAt != null) {
				hiddenForMs = now - hiddenAt;
				hiddenAt = null;
			}
			let latest = pendingVersion;
			if (!latest) {
				if (now - lastCheckAt < MIN_GAP_BETWEEN_CHECKS_MS) return;
				lastCheckAt = now;
				latest = await fetchLatestVersion();
			}
			act(
				planUpdate({
					latest,
					built: BUILT_VERSION,
					hiddenForMs,
					hasUnsaved: hasUnsaved.current(),
					alreadyReloaded: latest ? alreadyReloadedFor(latest) : false,
				}),
				latest,
			);
		};

		const onVisibilityChange = () => {
			if (document.hidden) {
				hiddenAt = Date.now();
			} else {
				check();
			}
		};
		const onFocus = () => check();
		const timer = window.setInterval(() => {
			if (!document.hidden) check();
		}, CHECK_EVERY_MS);

		document.addEventListener("visibilitychange", onVisibilityChange);
		window.addEventListener("focus", onFocus);
		return () => {
			window.clearInterval(timer);
			document.removeEventListener("visibilitychange", onVisibilityChange);
			window.removeEventListener("focus", onFocus);
		};
	}, []);

	// Una navigazione interna con un aggiornamento in sospeso: la pagina si
	// smonta comunque, è il momento giusto per prendere la versione nuova.
	useEffect(() => {
		if (!ENABLED || !pendingVersion) return;
		const plan = planUpdate({
			latest: pendingVersion,
			built: BUILT_VERSION,
			navigating: true,
			hasUnsaved: hasUnsaved.current(),
			alreadyReloaded: alreadyReloadedFor(pendingVersion),
		});
		if (plan === "reload") reloadTo(pendingVersion);
	}, [pathname]);
}
